import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { toast } from 'sonner';

interface Relay {
  id: string;
  name: string;
  address: string;
}

const DefaultPreferences = () => {
  const [relays, setRelays] = useState<Relay[]>([]);
  const [defaultRelayId, setDefaultRelayId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    if (user) {
      fetchRelays();
      fetchDefaultRelay();
    }
  }, [user]);

  const fetchRelays = async () => {
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('id, name, address')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setRelays(data || []);
    } catch (error) {
      console.error('Error fetching relays:', error);
    }
  };

  const fetchDefaultRelay = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('default_relay_id')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      if (data?.default_relay_id) {
        setDefaultRelayId(data.default_relay_id);
      }
    } catch (error) {
      console.error('Error fetching default relay:', error);
    }
  };

  const handleSaveDefaultRelay = async () => {
    if (!defaultRelayId) {
      toast.error(t('dashboard.selectRelayError'));
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ default_relay_id: defaultRelayId })
        .eq('id', user?.id);

      if (error) throw error;
      toast.success(t('dashboard.relayUpdated'));
    } catch (error) {
      console.error('Error saving default relay:', error);
      toast.error(t('dashboard.updateError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <CardTitle className="flex items-center text-base md:text-lg">
          <MapPin className="h-4 w-4 md:h-5 md:w-5 mr-2 flex-shrink-0" />
          <span className="truncate">{t('dashboard.defaultRelay')}</span>
        </CardTitle>
        <CardDescription className="text-xs md:text-sm">
          {t('dashboard.selectRelayPreference')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 md:space-y-4 p-4 md:p-6">
        <div className="space-y-2">
          <Label className="text-xs md:text-sm">{t('dashboard.relayPoint')}</Label>
          <Select value={defaultRelayId} onValueChange={setDefaultRelayId}>
            <SelectTrigger className="text-xs md:text-sm">
              <SelectValue placeholder={t('dashboard.selectRelay')} />
            </SelectTrigger>
            <SelectContent>
              {relays.map((relay) => (
                <SelectItem key={relay.id} value={relay.id} className="text-xs md:text-sm">
                  <span className="truncate">{relay.name} - {relay.address}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={handleSaveDefaultRelay} 
          disabled={loading || !defaultRelayId}
          className="w-full text-xs md:text-sm h-9 md:h-10"
        >
          {loading ? t('dashboard.saving') : t('dashboard.register')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default DefaultPreferences;
